import React, { useReducer } from 'react';
import { BreadCrumb, FooterSection, FaqSection } from '../components';
import InfoCard from '../components/InfoCard';
import { TrainClasses } from '../route_info/TrainClasses';
import { StaticTatkalPageData } from '../types';
import { searchWidgetInitialState, searchWidgetReducer } from '../../reducers/searchWidgetReducer';
import SearchWidget from '../components/SearchWidget/SearchWidget';
import HtmlHeader from '../components/HtmlHeader';
import { FOOTER_DATA_IRCTC, IRCTC_EXTERNAL_JS } from '../../utils/RailsSEOConstants';
import { getStaticPageLoadEvent } from '../../utils/omnitureUtils';

type StaticTatkalPageDataProps = {
  data: StaticTatkalPageData;
};
const SearchWidgetContext = React.createContext<any>(searchWidgetInitialState);

const StaticTatkalPage = ({ data }: StaticTatkalPageDataProps) => {
  const [searchWidgetState, searchWidgetDispatch] = useReducer(searchWidgetReducer, searchWidgetInitialState);
  return (
    <div>
      <SearchWidgetContext.Provider value={{ searchWidgetState, searchWidgetDispatch }}>
        <SearchWidget
          openDatePickerOnMount={false}
          topFix={true}
          classNode={TrainClasses.FC}
          pageWiseContext={SearchWidgetContext}
        />
      </SearchWidgetContext.Provider>
      <div className="container">
        <HtmlHeader data={data} />
        <BreadCrumb breadcrumb={data.template.breadcrumb} />

        <div className="heading">
          <h1 className="appendTop15 appendBottom15 font22">{data.template.h1_title}</h1>
        </div>

        <section>
          <h2 className="desktopShow appendBottom15">{`Information regarding Tatkal Trains`}</h2>
          <InfoCard infos={data.infos} />
        </section>
        <FaqSection faq={data.faq} pageUrl={data.template.sample_url} type="tatkalTrains" />
      </div>
      <FooterSection template={data.template} />
    </div>
  );
};

StaticTatkalPage.fetchData = () => {
  const faq = [
    {
      question: 'How do I book tatkal tickets online?',
      answer: `You can book your tatkal ticket one day before the scheduled departure of your train. Here’s how:\r\n<br/>\r\n
            1.&nbspLogin to your IRCTC account at <a href="https://www.makemytrip.com/railways/">https://www.makemytrip.com/railways/</a>.\r\n<br/>\r\n
      2.&nbspSelect your source and destination stations.\r\n<br/>\r\n
      3.&nbspChoose your date of journey.\r\n<br/>\r\n
      4.&nbspSelect quota as ‘TATKAL’ and search for trains.\r\n<br/>\r\n
      5.&nbspClick ‘BOOK NOW’ for your preferred train.\r\n<br/>\r\n
      6.&nbspFill passengers detail carefully like Name, Age, Gender, Seat Preference, etc.\r\n<br/>\r\n
      1.&nbspSelect a suitable Payment Method (internet banking, payment wallet, credit or debit card, upi) and proceed to pay for the ticket.\r\n<br/>\r\n
      2.&nbspPrint your e-ticket`
    },
    {
      question: `How do I cancel my tatkal train tickets?`,
      answer: `There is no refund applicable on a confirmed tatkal ticket but if you want to cancel a wait-listed or RAC tatkal ticket, charges will be deducted as per existing Railway rules. If your wait-listed or RAC tatkal ticket doesn’t get confirmed, it automatically gets cancelled and generates a refund.`
    },
    {
      question: `What is tatkal reservation or tatkal quota? `,
      answer: `A few seats are reserved for tatkal quota on every train. These seats are opened for general public a day before chart preparation. Passengers can book tatkal tickets for these seats by paying an additional amount over and above the original fare. This is tatkal reservation. Tatkal quota is available for all categories except for Unreserved/General Class (UR/GEN) and reserved First AC class (1 AC).`
    },
    {
      question: `What is the difference between ‘tatkal booking’ and ‘premium tatkal booking’`,
      answer: `A tatkal booking has a fixed additional amount, which you need to pay along with the fare of the ticket while premium tatkal booking has dynamic price and it fluctuates as per the demand or availability. The price goes up when the demand is high and goes low when there is a drop in demand. You can do the premium tatkal booking on the MakeMyTrip or IRCTC website.`
    }
  ];
  const infos = [
    {
      query: `Book Tatkal Train Tickets Online with MakeMyTrip:`,
      info: `Since beginning its journey in 2000, MakeMyTrip has had one major goal – to empower every traveller by giving them a range of comprehensive and instant booking choices. Train-travel is the heart-beat of this nation and the sheer volume of booking traffic daily can throw up many challenges for travellers who need to make quick and swift travel plans. Which is why at MakeMyTrip, we have partnered with IRCTC to offer you a seamless train travel experience with our Tatkal bookings.`
    },
    {
      query: `Why Choose Tatkal:`,
      info: `We know the desperate struggle of not getting confirmed tickets, especially if it’s a last-minute travel plan. Well, that is where the tatkal booking comes in handy. As the name suggests, a tatkal ticket booking is meant for immediate plans and can be made at very short notice, just one day before departure date. This is a big relief for those who need to travel by train during summer vacations, peak festival seasons, long holiday weekends etc.`
    },
    {
      query: `What to Remember:`,
      info: `Trains with tatkal quota have a pre-determined number of seats reserved in AC and Non-AC compartments. You can book tatkal online on the MakeMyTrip Website, IRCTC website or offline at railway ticket counters. But remember, the tatkal window opens only for an hour. You can book for only 4 passengers per PNR.`
    },
    {
      query: `What MMT Offers:`,
      info: `You can use our app and websites to search for the best train for your route, check which stations the train goes through, find out the closest available dates (in case your desired dates are not available) and then proceed to book your tatkal train tickets. In addition, you can also choose from our entire bouquet of our holiday and hotel packages and also rent a car to take you to our destination – all at one place.`
    }
  ];
  const template = {
    // created: `Tue May 14 2019 11:08:50 GMT+0530 (India Standard Time)`,
    // modified: `Thu Jan 02 2020 06:16:07 GMT+0530 (India Standard Time)`,
    // id: `1`,
    template_name: `route`,
    // type: `ab`,
    pageUrl: `https://www.makemytrip.com/railways/tatkal-railway-reservation.html`,
    meta_title: `Tatkal Train e-Ticket Booking | IRCTC Tatkal Train Reservation at MakeMyTrip`,
    meta_description: `Tatkal ticket online booking at MakeMytrip. Book your Tatkal train e-tickets for all Covid special trains. Check tatkal quota with ✓Timing ✓Seat availability and train fares.`,
    meta_keywords: `tatkal train booking, tatkal train booking time, tatkal train fare enquiry, premium tatkal trains, tatkal quota in trains, tatkal seats in trains, premium tatkal trains list`,
    analytics: getStaticPageLoadEvent('seo:rails:tatkal:trains'),
    footer_data: FOOTER_DATA_IRCTC,
    h1_title: `IRCTC Tatkal Trains Booking`,
    breadcrumb: [
      {
        redirectionUrl: `https://www.makemytrip.com/`,
        displayString: `Home`
      },
      {
        redirectionUrl: `https://www.makemytrip.com/railways/`,
        displayString: `Rails`
      },
      {
        displayString: `Tatkal Trains Booking`,
        redirectionUrl: `https://www.makemytrip.com/railways/tatkal-railway-reservation.html`
      }
    ],
    banner_desktop: ``,
    banner_mobile: ``,
    sample_url: `https://www.makemytrip.com/railways/tatkal-railway-reservation.html`,
    // sub_title: `Following is the list of all the trains running between Delhi to Mumbai Railway Stations:`,
    // eslint-disable-next-line
    external_js: IRCTC_EXTERNAL_JS,
    extra_info: ``
  };
  // let finalResponse;

  const finalResponse = {
    faq,
    infos,
    template
  };
  const pageType = 'trains';
  const cacheKey = 'HTML_STATIC_TATKAL-RAILWAY-RESERVATION.HTML';
  return {
    pushToCache: true, // if this is set then after ssr it will be stored in cache
    cacheKey,
    pageType,
    ...finalResponse
  };
};

export default StaticTatkalPage;
